import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Card, Tcg } from '../types'

/**
 * A "Lista" — an independent, named deck (a parsed decklist).
 *
 * Persisted to localStorage (NO backend). Survives reloads. Cards are kept
 * as text-only data (name + quantity + optional set/collector); we never
 * store or resolve images in the frontend (Cristian's decision).
 *
 * Each list belongs to a TCG (`tcg`). Lists of different games can be
 * combined in a single order — the backend then produces one PDF per game
 * packaged as a ZIP.
 */
export interface DeckList {
  id: string
  name: string
  mainboard: Card[]
  sideboard: Card[]
  createdAt: string
  /** TCG this list belongs to. Defaults to "mtg" for legacy lists. */
  tcg: Tcg
}

interface ListsState {
  lists: DeckList[]
  /** Create a new list. Returns the new list's id. */
  addList: (name: string | null, mainboard: Card[], sideboard: Card[], tcg?: Tcg) => string
  /** Rename a list. */
  renameList: (id: string, name: string) => void
  /** Replace a list's contents (re-parse / edit). */
  setListCards: (id: string, mainboard: Card[], sideboard: Card[]) => void
  /** Update a single card within a list's zone. */
  updateCard: (id: string, zone: 'main' | 'side', index: number, card: Card) => void
  /** Remove a card from a list's zone. */
  removeCard: (id: string, zone: 'main' | 'side', index: number) => void
  /** Change a card's quantity (min 1) within a list's zone. */
  changeQuantity: (id: string, zone: 'main' | 'side', index: number, delta: number) => void
  /** Delete one list. */
  removeList: (id: string) => void
  /** Delete ALL lists (after confirmation in the UI). */
  clearAll: () => void
}

let counter = 0
function nextDefaultName(existing: DeckList[]): string {
  // Find the smallest "Lista N" not already used.
  const used = new Set(existing.map((l) => l.name))
  let n = existing.length + 1
  while (used.has(`Lista ${n}`)) n += 1
  return `Lista ${n}`
}

function newId(): string {
  counter += 1
  return `${Date.now().toString(36)}-${counter}-${Math.random().toString(36).slice(2, 7)}`
}

export const useListsStore = create<ListsState>()(
  persist(
    (set) => ({
      lists: [],

      addList: (name, mainboard, sideboard, tcg = 'mtg') => {
        const id = newId()
        set((state) => {
          const finalName = name?.trim() || nextDefaultName(state.lists)
          const list: DeckList = {
            id,
            name: finalName,
            mainboard,
            sideboard,
            createdAt: new Date().toISOString(),
            tcg,
          }
          return { lists: [...state.lists, list] }
        })
        return id
      },

      renameList: (id, name) =>
        set((state) => ({
          lists: state.lists.map((l) => (l.id === id ? { ...l, name } : l)),
        })),

      setListCards: (id, mainboard, sideboard) =>
        set((state) => ({
          lists: state.lists.map((l) => (l.id === id ? { ...l, mainboard, sideboard } : l)),
        })),

      updateCard: (id, zone, index, card) =>
        set((state) => ({
          lists: state.lists.map((l) => {
            if (l.id !== id) return l
            const key = zone === 'main' ? 'mainboard' : 'sideboard'
            const arr = l[key].map((c, i) => (i === index ? card : c))
            return { ...l, [key]: arr }
          }),
        })),

      removeCard: (id, zone, index) =>
        set((state) => ({
          lists: state.lists.map((l) => {
            if (l.id !== id) return l
            const key = zone === 'main' ? 'mainboard' : 'sideboard'
            return { ...l, [key]: l[key].filter((_, i) => i !== index) }
          }),
        })),

      changeQuantity: (id, zone, index, delta) =>
        set((state) => ({
          lists: state.lists.map((l) => {
            if (l.id !== id) return l
            const key = zone === 'main' ? 'mainboard' : 'sideboard'
            const arr = l[key].map((c, i) =>
              i === index ? { ...c, quantity: Math.max(1, c.quantity + delta) } : c
            )
            return { ...l, [key]: arr }
          }),
        })),

      removeList: (id) =>
        set((state) => ({ lists: state.lists.filter((l) => l.id !== id) })),

      clearAll: () => set({ lists: [] }),
    }),
    {
      name: 'mpcfill-lists', // localStorage key
      version: 2,
      migrate: (persisted: unknown) => {
        // v1 → v2: add `tcg: 'mtg'` to lists that predate multi-TCG support.
        const state = persisted as { lists?: Array<Record<string, unknown>> } | undefined
        if (state?.lists) {
          state.lists = state.lists.map((l) => ({ tcg: 'mtg', ...l }))
        }
        return state
      },
    }
  )
)

/** Total card count (sum of quantities) for a zone. */
export function countCards(cards: Card[]): number {
  return cards.reduce((acc, c) => acc + c.quantity, 0)
}
